import { IMAGE_SETTINGS } from 'common/types/pdfExport';
import { equalizeSpreadsheetTitleContentRowHeights } from 'utilities';

// Opt-in attribute for a `[data-ref]` target holding a spreadsheet whose text columns wrap.
// Its value lists the content-table column legends (e.g. "A" or "A,C") that may be widened.
// A long table with a narrow wrapping column is much taller than the page is wide, so it is
// fitted to the page by height and prints small. Widening the listed columns trades height
// for width until the table's shape matches the page as closely as possible.
export const EXPORT_FIT_COLUMNS_ATTR = 'data-export-fit-columns';

// Width-to-height ratio of the PDF content box the capture is fitted into.
const PAGE_CONTENT_ASPECT_RATIO = IMAGE_SETTINGS.MAX_WIDTH_MM / IMAGE_SETTINGS.MAX_HEIGHT_MM;
const MAX_FIT_COLUMN_WIDTH_PX = 1600;

interface TableShape {
  width: number;
  height: number;
}

// Relative print scale of a capture of this shape: whichever of width or height binds first.
const getFitScale = ({ width, height }: TableShape) => Math.min(PAGE_CONTENT_ASPECT_RATIO / width, 1 / height);

const clearPinnedHeights = (nodes: HTMLElement[]) => {
  nodes.forEach(node => {
    node.style.removeProperty('height');
    node.style.removeProperty('min-height');
    node.style.removeProperty('max-height');
  });
};

/**
 * Widens the target's fit columns so the spreadsheet's width-to-height ratio comes as close
 * as possible to the PDF content box's, which maximizes the scale it prints at. Column widths
 * only change the live DOM of the capture iframe, so the on-screen grid is untouched.
 *
 * The titles and values tables are separate, so the on-screen row sync pins row heights
 * (`height: … !important`). Those pins are cleared while measuring, and a row's height is the
 * taller of its title and values sides; the rows are equalized again once the width is set.
 */
export const fitExportColumnsToPage = (el: HTMLElement): void => {
  const legends = (el.getAttribute(EXPORT_FIT_COLUMNS_ATTR) ?? '')
    .split(',')
    .map(legend => legend.trim())
    .filter(Boolean);
  if (legends.length === 0) {
    return;
  }

  el.querySelectorAll<HTMLElement>('.titles-columns-container').forEach(titlesContainer => {
    const parent = titlesContainer.parentElement;
    const contentContainer = parent?.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
    if (!parent || !contentContainer) {
      return;
    }

    const fitCells = legends.flatMap(legend =>
      Array.from(contentContainer.querySelectorAll<HTMLElement>(`td[data-column-legend="${legend}"]`))
    );
    // The inline width set by the column widths, not the rendered one, which adds cell padding.
    const startWidth = Math.max(
      0,
      ...fitCells.map(cell => Number.parseFloat(cell.style.width) || cell.getBoundingClientRect().width)
    );
    if (fitCells.length === 0 || startWidth >= MAX_FIT_COLUMN_WIDTH_PX) {
      return;
    }

    const tables = Array.from(parent.querySelectorAll('table'));
    const titleRows = Array.from(titlesContainer.querySelectorAll<HTMLTableRowElement>('table tr'));
    const contentRows = Array.from(contentContainer.querySelectorAll<HTMLTableRowElement>('table tr'));
    const pinnedNodes = Array.from(parent.querySelectorAll<HTMLElement>('tr, td'));
    // Space the target takes outside the tables (padding, toolbars), measured while aligned.
    const tablesHeight = Math.max(...tables.map(table => table.getBoundingClientRect().height));
    const chromeHeight = Math.max(0, el.getBoundingClientRect().height - tablesHeight);

    const measure = (columnWidth: number): TableShape => {
      fitCells.forEach(cell => {
        Object.assign(cell.style, {
          width: `${columnWidth}px`,
          minWidth: `${columnWidth}px`,
          maxWidth: `${columnWidth}px`,
        });
      });
      clearPinnedHeights(pinnedNodes);

      const bounds = tables.map(table => table.getBoundingClientRect());
      const width = Math.max(...bounds.map(bound => bound.right)) - Math.min(...bounds.map(bound => bound.left));
      // Rows pair by position when both sides render the same rows; otherwise fall back to the
      // taller table, which is what the row sync would stretch the other one to.
      const rowsHeight =
        titleRows.length === contentRows.length
          ? titleRows.reduce(
            (sum, titleRow, index) => sum + Math.max(titleRow.offsetHeight, contentRows[index].offsetHeight),
            0
          )
          : Math.max(...tables.map(table => table.offsetHeight));
      return { width, height: chromeHeight + rowsHeight };
    };

    // Widening only adds width and removes height, so width/height grows with the column
    // width. Binary-search the first width whose shape is at least as wide as the page, then
    // keep whichever neighbor prints larger.
    const isPageWide = (shape: TableShape) => shape.width / shape.height >= PAGE_CONTENT_ASPECT_RATIO;
    let bestWidth = startWidth;
    if (!isPageWide(measure(startWidth))) {
      let low = startWidth;
      let high = MAX_FIT_COLUMN_WIDTH_PX;
      if (isPageWide(measure(high))) {
        while (high - low > 1) {
          const middle = Math.floor((low + high) / 2);
          if (isPageWide(measure(middle))) {
            high = middle;
          } else {
            low = middle;
          }
        }
        bestWidth = getFitScale(measure(low)) >= getFitScale(measure(high)) ? low : high;
      } else {
        // Still taller than the page with nothing left to unwrap: use the narrowest width
        // that reaches the minimum height, since any extra width would only shrink the print.
        const minHeight = measure(high).height;
        while (high - low > 1) {
          const middle = Math.floor((low + high) / 2);
          if (measure(middle).height <= minHeight) {
            high = middle;
          } else {
            low = middle;
          }
        }
        bestWidth = high;
      }
    }

    measure(bestWidth);
    equalizeSpreadsheetTitleContentRowHeights(titlesContainer, contentContainer);
  });
};
